import React, { useState, useEffect } from 'react';
import { useTwoFactorAuthentication } from './useTwoFactorAuthentication';
import useIntl from '../../hooks/useIntl';
import TMForm from '../../lib/Form/TMForm';
import TMPaper from '../../lib/Paper/TMPaper';
import { TWO_FACTOR_AUTH } from '../../util/twoFactorAuth';
import { useProgressDialog } from '../../context/ProgressDialogProvider';
import { useSnackbar } from 'notistack';
import { logout } from '../../util';

export default function TwoFactorAuthentication({
    loggedInUser,
    setLoggedInUser,
    isTwoFactorEmailVerified,
    setIsTwoFactorEmailVerified
  }) 
{
  const { translate } = useIntl();
  const [verificationCode, setVerificationCode] = useState('');
  const { showProgressDialog, hideProgressDialog } = useProgressDialog();
  const twoFactorAuthentication = useTwoFactorAuthentication();
  const { enqueueSnackbar } = useSnackbar();

  const sendVerificationEmail = async () => {
    return await twoFactorAuthentication.sendEmail();
  };

  useEffect(() => {
    if (twoFactorAuthentication.data.hasOwnProperty('token')) {
      // set 2FA token in local storage for verification request
      let walletInfo = window.walletApi._getWalletInfo();
      walletInfo.token = twoFactorAuthentication.data.token;
      window.walletApi._storeWalletInfo(walletInfo);
    }
  }, [twoFactorAuthentication.data]);

  useEffect(() => {
    if (loggedInUser.organization.walletUrl === "0") return;

    sendVerificationEmail();

  }, [loggedInUser]);

  useEffect(() => {
    const handleVerificationResponse = async () => {
      if (twoFactorAuthentication.data.twoFactorAuthEmailValidated) {
        try {
          // SCRDEV-3728: wallet is already unlocked, all we need to do is store the token in local storage now
          // Also update the wallet URL, otherwise authentication freaks out
          let walletInfo = window.walletApi._getWalletInfo();
          // company wallets won't return the unlock token, as it was not set on the backend
          if (twoFactorAuthentication.data.unlockToken) {
            walletInfo.token = twoFactorAuthentication.data.unlockToken;
          } else {
            // For company wallets, the unlock token was temporarily stored in the loggedInUser object,
            // because it would be overwritten with the 2FA token
            walletInfo.token = loggedInUser.unlockToken;
          }
          walletInfo.url = twoFactorAuthentication.data.user.organization.wallet_url;
          await window.walletApi._storeWalletInfo(walletInfo);
          // twoFactorAuthentication.data.user is the gov_user object, with a nested 'user' object which is what setLoggedInUser expects
          setLoggedInUser(twoFactorAuthentication.data.user.user, setIsTwoFactorEmailVerified(true))
        } catch (err) {
          console.error(err)
          logout('There was a problem logging in, please try again.')
        }
      }
    };

    handleVerificationResponse();
  }, [twoFactorAuthentication.data.twoFactorAuthEmailValidated, loggedInUser]);

  useEffect(() => {
    if (twoFactorAuthentication.error) {
      enqueueSnackbar(twoFactorAuthentication.error, {
        variant: 'error',
        autoHideDuration: 3000,
        onClose: () => {
          twoFactorAuthentication.setError(() => '');
        },
      });
    }
  }, [twoFactorAuthentication.error]);

  const handleChange = (e) => {
    const { value } = e.target;
    setVerificationCode(value);
  };

  const handleSubmit = async () => {
    await twoFactorAuthentication.validate({ code: verificationCode });
  };

  const handleResendEmail = async () => {
    showProgressDialog();
    const emailSent = await sendVerificationEmail();
    hideProgressDialog();
    if (emailSent) {
      enqueueSnackbar(`Verification code sent successfully`, {
        variant: 'success',
        autoHideDuration: 3000,
        style: { maxWidth: '420px' },
      });
    }
  };

  return (
    <TMPaper title="Two-Factor Authentication">
      <TMForm
        object={{ code: verificationCode }}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        submitLabel="Verify"
        enableSubmitCondition={true}
        actions={[
          {
            handle: handleResendEmail,
            label: 'Re-send code',
            variant: 'outlined',
            color: 'primary'
          },
        ]}
        cancelLabel="Cancel"
        handleCancel={logout}
        fields={[
          {
            field: 'code',
            label: 'Enter 2FA Code',
            required: true,
            fullWidth: true,
            helperText: '6-digit code has been sent to your registered email'
          },
        ]}
      />
      <p>
        <b>Important:</b> <br/>
        Two factor authentication emails are sent from <b>twofactor@securecontainerrelease.com</b>. <br/>
        In case you're not receiving these emails, please check your spam folder and whitelist the emailaddress.
      </p>
    </TMPaper>
  );
}
